"use client"

import { useState, useEffect } from "react"
import { ChatBubble } from "./components/ChatBubble"
import { ChatWindow } from "./components/ChatWindow"
import { PaymentModal } from "./components/PaymentModal"
import { AddressForm } from "./components/AddressForm"
import { BookingForm } from "./components/BookingForm"
import { CartView } from "./components/CartView"
import { useChat } from "./hooks/useChat"
import type { AgentWidgetProps, AgentConfig } from "./types"
import "./styles/widget.css"

export function AgentWidget(props: AgentWidgetProps) {
    const {
        apiKey,
        agentId,
        baseUrl = typeof window !== 'undefined' && window.location.hostname === 'localhost' 
            ? 'http://localhost:3001' 
            : "https://jutech-agent-server.onrender.com",
        user,
        onUserUpdate,
        className = "",
        disableAutoConfig = false,
       // Default values that can be overridden by server config or props
        theme,
        position,
        primaryColor,
        allowFileUpload: propAllowFileUpload = false,
        placeholder,
        title,
        subtitle,
    } = props

    const [isOpen, setIsOpen] = useState(false)
    const [unreadCount, setUnreadCount] = useState(0)
    const [config, setConfig] = useState<AgentConfig | null>(null)
    const [configLoading, setConfigLoading] = useState(!disableAutoConfig)

    useEffect(() => {
        if (disableAutoConfig) {
            setConfigLoading(false)
            return
        }

        const fetchConfig = async () => {
            try {
                const url = `${baseUrl}/api/chat/agents/${agentId}/public-config?apiKey=${apiKey}`
                console.log("Fetching config from:", url)
                const response = await fetch(url)
                console.log("Response status:", response.status)
                if (response.ok) {
                    const agentConfig = await response.json()
                    console.log("Raw agent config:", agentConfig)
                    setConfig(agentConfig)
                } else {
                    const errorText = await response.text()
                    console.error("Config fetch failed:", response.status, errorText)
                }
            } catch (error) {
                console.warn("Failed to fetch agent config, using props:", error)
            } finally {
                setConfigLoading(false)
            }
        }

        fetchConfig()
    }, [agentId, baseUrl, disableAutoConfig])
    console.log("Fetched agent config:", config)

    const agentConfig = (config as any)?.config || config

    const finalConfig = {
        theme: agentConfig?.behavior?.theme || theme || "light",
        position: agentConfig?.behavior?.position || position || "bottom-right",
        primaryColor: agentConfig?.behavior?.primaryColor || primaryColor || "#3b82f6",
        allowFileUpload: agentConfig?.paymentEnabled || propAllowFileUpload || false,
        bookingEnabled: agentConfig?.features?.bookingEnabled || false,
        placeholder: agentConfig?.behavior?.placeholder || placeholder || "Type your message...",
        title: agentConfig?.name || title || "AI Assistant",
        subtitle: agentConfig?.businessDescription || subtitle || "How can I help you today?",
    }

    // Apply theming via inline styles to override any conflicts
    const widgetStyle = {
        '--aw-primary-color': finalConfig.primaryColor,
        '--aw-primary-hover': adjustColor(finalConfig.primaryColor, -10),
    } as React.CSSProperties

    function adjustColor(color: string, percent: number): string {
        const num = parseInt(color.replace("#", ""), 16)
        const amt = Math.round(2.55 * percent)
        const R = (num >> 16) + amt
        const G = (num >> 8 & 0x00FF) + amt
        const B = (num & 0x0000FF) + amt
        return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
            (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
            (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1)
    }

    console.log("Final config:", finalConfig)
    console.log("Agent config paystackPublicKey:", agentConfig?.paystackPublicKey)
    console.log("Full agent config:", agentConfig)

    const {
        messages,
        isLoading,
        error,
        sendMessage,
        clearMessages,
        initializeChat,
        user: chatUser,
        isCollectingUserInfo,
        products,
        loadingProducts,
        loadProducts,
        createOrder,
        completePaymentOrder,
        completePaymentBooking,
        // completePayment, --- IGNORE ---
        conversationId,
        orders,
        loadOrders,
        services,
        loadServices,
        createBooking,
        bookings,
        loadBookings,
        setUserInfo,
        endConversation,
    } = useChat(apiKey, agentId, baseUrl, user, agentConfig?.behavior?.voiceEnabled, onUserUpdate)

    console.log("Products loaded:", products)
    console.log("Products length:", products.length)

    const [showProducts, setShowProducts] = useState(false)
    const [showOrders, setShowOrders] = useState(false)
    const [showServices, setShowServices] = useState(false)
    const [showBookings, setShowBookings] = useState(false)
    const [showPaymentOptions, setShowPaymentOptions] = useState(false)
    const [showInvoice, setShowInvoice] = useState(false)
    const [showFeedback, setShowFeedback] = useState(false)
    const [showCart, setShowCart] = useState(false)
    const [cartItems, setCartItems] = useState<any[]>([])
    const [paymentModal, setPaymentModal] = useState<{
        isOpen: boolean
        product: any
        order: any
    }>({ isOpen: false, product: null, order: null })
    const [addressForm, setAddressForm] = useState<{
        isOpen: boolean
        product: any
    }>({ isOpen: false, product: null })
    const [bookingForm, setBookingForm] = useState<{
        isOpen: boolean
        service: any
    }>({ isOpen: false, service: null })
    const [isAddressLoading, setIsAddressLoading] = useState(false)
    const [isPaymentLoading, setIsPaymentLoading] = useState(false)

    useEffect(() => {
        if (isOpen && messages.length === 0 && !user) {
            initializeChat()
        }
        if (isOpen && products.length === 0) {
            loadProducts()
        }
        if (isOpen && services.length === 0) {
            loadServices()
        }
    }, [isOpen, messages.length, user, initializeChat, products.length, loadProducts])

    // Set user info when provided via props or when conversation starts
    useEffect(() => {
        if (user && setUserInfo) {
            setUserInfo(user)
        }
    }, [user, setUserInfo])

    const handleBuyProduct = async (product: any) => {
        setAddressForm({ isOpen: true, product })
    }

    const handleAddressSubmit = async (address: any) => {
        setIsAddressLoading(true)
        try {
            const order = await createOrder(addressForm.product, address)
            console.log('Order created with Paystack key:', order.paystackPublicKey)
            console.log('Agent config Paystack key:', agentConfig?.paystackPublicKey)
            setAddressForm({ isOpen: false, product: null })
            setPaymentModal({ isOpen: true, product: addressForm.product, order })
        } catch (error) {
            alert(`Failed to create order: ${error instanceof Error ? error.message : 'Unknown error'}`)
        } finally {
            setIsAddressLoading(false)
        }
    }

    const handlePayOrder = async (order: any) => {
        const product = { _id: order.productId._id, name: order.productId.name }
        setPaymentModal({ isOpen: true, product, order: { ...order, orderId: order._id } })
    }

    const handlePaymentSuccess = async (reference: string) => {
        setIsPaymentLoading(true)
        try {
            if (paymentModal.order?.bookingId) {
                // This is a booking payment
                await completePaymentBooking(paymentModal.order.bookingId, reference)
                alert('Payment successful! Your booking has been confirmed.')
                loadBookings()
            } else if (paymentModal.order.orderId) {
                // This is a product order payment
                await completePaymentOrder(paymentModal.order.orderId, reference)
                alert('Payment successful! Your order has been confirmed.')
                loadOrders()
            }

            setPaymentModal({ isOpen: false, product: null, order: null })
        } catch (error) {
            alert('Payment verification failed. Please contact support.')
        } finally {
            setIsPaymentLoading(false)
        }
    }

    const handlePaymentError = (error: string) => {
        alert(`Payment failed: ${error}`)
    }

    const handleBookService = async (service: any) => {
        setBookingForm({ isOpen: true, service })
    }

    const handleBookingSubmit = async (bookingData: any) => {
        try {
            if (bookingData.requiresPayment) {
                // Create booking first
                const bookingResponse = await fetch(`${baseUrl}/api/chat/agents/${agentId}/bookings?apiKey=${apiKey}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        serviceId: bookingForm.service._id,
                        customerName: chatUser?.name,
                        customerEmail: chatUser?.email,
                        serviceName: bookingForm.service.name,
                        date: bookingData.date,
                        time: bookingData.time,
                        duration: bookingData.duration,
                        location: bookingData.location,
                        notes: bookingData.notes
                    })
                })

                if (!bookingResponse.ok) {
                    throw new Error(`Failed to create booking: ${bookingResponse.statusText}`)
                }

                const booking = await bookingResponse.json()
                
                // Create payment order for the booking
                const order = {
                    orderId: `booking_${booking.booking._id}`,
                    bookingId: booking.booking._id,
                    amount: bookingData.paymentAmount || bookingForm.service.price,
                    currency: bookingForm.service.currency || 'GHS',
                    itemType: 'service',
                    itemName: bookingForm.service.name,
                    paystackPublicKey: agentConfig?.paystackPublicKey
                }
                
                setBookingForm({ isOpen: false, service: null })
                setPaymentModal({ isOpen: true, product: bookingForm.service, order })
            } else {
                // Create booking directly without payment
                const response = await fetch(`${baseUrl}/api/chat/agents/${agentId}/bookings?apiKey=${apiKey}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        serviceId: bookingForm.service._id,
                        customerName: chatUser?.name,
                        customerEmail: chatUser?.email,
                        serviceName: bookingForm.service.name,
                        date: bookingData.date,
                        time: bookingData.time,
                        duration: bookingData.duration,
                        location: bookingData.location,
                        notes: bookingData.notes
                    })
                })

                if (!response.ok) {
                    throw new Error(`Failed to create booking: ${response.statusText}`)
                }

                alert('Booking created successfully!')
                setBookingForm({ isOpen: false, service: null })
            }
        } catch (error) {
            alert(`Failed to create booking: ${error instanceof Error ? error.message : 'Unknown error'}`)
        }
    }

    useEffect(() => {
        if (!isOpen && messages.length > 0) {
            const lastMessage = messages[messages.length - 1]
            if (lastMessage.role === "assistant" && !lastMessage.isStreaming) {
                setUnreadCount((prev) => prev + 1)
            }
        }
    }, [messages, isOpen])

    // Auto-open views based on metadata
    useEffect(() => {
        const lastMessage = messages[messages.length - 1]
        if (lastMessage?.metadata && !lastMessage.isStreaming) {
            if (lastMessage.metadata.showProducts) setShowProducts(true)
            if (lastMessage.metadata.showServices) setShowServices(true)
            if (lastMessage.metadata.showCart) setShowCart(true)
            if (lastMessage.metadata.showPaymentOptions) setShowPaymentOptions(true)
            if (lastMessage.metadata.showInvoice) setShowInvoice(true)
            if (lastMessage.metadata.requestFeedback) setShowFeedback(true)
        }
    }, [messages])

    const handleOpen = () => {
        setIsOpen(true)
        setUnreadCount(0)
    }

    const handleClose = () => {
        setIsOpen(false)
    }

    useEffect(() => {
        if (finalConfig.theme === "dark") {
            document.documentElement.classList.add("dark")
        } else if (finalConfig.theme === "light") {
            document.documentElement.classList.remove("dark")
        }
    }, [finalConfig.theme])

    if (configLoading) {
        return (
            <div className={`agent-widget ${className}`}>
                <div className="fixed bottom-4 right-4 w-14 h-14 bg-gray-200 rounded-full animate-pulse" />
            </div>
        )
    }

    return (
        <div
            className={`agent-widget ${finalConfig.theme === 'dark' ? 'dark' : ''} ${className}`}
            style={widgetStyle}
        >
            <ChatBubble
                isOpen={isOpen}
                onClick={handleOpen}
                primaryColor={finalConfig.primaryColor}
                unreadCount={unreadCount}
                position={finalConfig.position}
            />

            <ChatWindow
                isOpen={isOpen}
                onClose={handleClose}
                messages={messages}
                isLoading={isLoading}
                error={error}
                onSendMessage={sendMessage}
                onClearMessages={clearMessages}
                primaryColor={finalConfig.primaryColor}
                title={finalConfig.title}
                subtitle={finalConfig.subtitle}
                placeholder={finalConfig.placeholder}
                position={finalConfig.position}
                products={products}
                onBuyProduct={handleBuyProduct}
                showProducts={showProducts}
                onToggleProducts={() => setShowProducts(!showProducts)}
                voiceEnabled={agentConfig?.behavior?.voiceEnabled}
                orders={orders}
                showOrders={showOrders}
                onToggleOrders={() => {
                    setShowOrders(!showOrders)
                    if (!showOrders) {
                        loadOrders()
                    }
                }}
                onPayOrder={handlePayOrder}
                services={services}
                onBookService={handleBookService}
                showServices={showServices}
                onToggleServices={() => {
                    setShowServices(!showServices)
                    if (!showServices) {
                        loadServices()
                    }
                }}
                bookings={bookings}
                showBookings={showBookings}
                onToggleBookings={() => {
                    setShowBookings(!showBookings)
                    if (!showBookings) {
                        loadBookings()
                    }
                }}
                bookingEnabled={finalConfig.bookingEnabled}
                onEndConversation={endConversation}
                showPaymentOptions={showPaymentOptions}
                onTogglePaymentOptions={() => setShowPaymentOptions(!showPaymentOptions)}
                showInvoice={showInvoice}
                onToggleInvoice={() => setShowInvoice(!showInvoice)}
                showFeedback={showFeedback}
                onToggleFeedback={() => setShowFeedback(!showFeedback)}
                onSubmitFeedback={async (feedback) => {
                    console.log('Feedback submitted:', feedback)
                    // TODO: Send feedback to server
                }}
                showCart={showCart}
                onToggleCart={() => setShowCart(!showCart)}
                cartItems={cartItems}
                onUpdateCartQuantity={(id, quantity) => {
                    if (quantity === 0) {
                        setCartItems(prev => prev.filter(item => item.id !== id))
                    } else {
                        setCartItems(prev => prev.map(item => 
                            item.id === id ? { ...item, quantity } : item
                        ))
                    }
                }}
                onRemoveCartItem={(id) => {
                    setCartItems(prev => prev.filter(item => item.id !== id))
                }}
                onAddCartItem={(productName) => {
                    const product = products.find(p => 
                        p.name.toLowerCase().includes(productName.toLowerCase())
                    )
                    if (product) {
                        const existingItem = cartItems.find(item => item.id === product._id)
                        if (existingItem) {
                            setCartItems(prev => prev.map(item => 
                                item.id === product._id 
                                    ? { ...item, quantity: item.quantity + 1 }
                                    : item
                            ))
                        } else {
                            setCartItems(prev => [...prev, {
                                id: product._id,
                                name: product.name,
                                price: product.price,
                                quantity: 1,
                                image: product.images?.[0]
                            }])
                        }
                    }
                }}
                onCartCheckout={async () => {
                    if (cartItems.length === 0) return
                    
                    try {
                        // Create a combined order for all cart items
                        const total = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0)
                        const itemNames = cartItems.map(item => `${item.name} (x${item.quantity})`).join(', ')
                        
                        const order = await createOrder(
                            null, // No single product
                            null, // No delivery address for now
                            {
                                amount: total,
                                currency: products[0]?.currency || 'GHS',
                                itemName: `Cart Items: ${itemNames}`,
                                itemType: 'cart',
                                cartItems: cartItems
                            }
                        )
                        
                        setShowCart(false)
                        setCartItems([]) // Clear cart after checkout
                        setPaymentModal({ 
                            isOpen: true, 
                            product: { name: `Cart (${cartItems.length} items)` }, 
                            order 
                        })
                    } catch (error) {
                        alert(`Failed to create order: ${error instanceof Error ? error.message : 'Unknown error'}`)
                    }
                }}
            />

            <PaymentModal
                isOpen={paymentModal.isOpen}
                onClose={() => setPaymentModal({ isOpen: false, product: null, order: null })}
                product={paymentModal.product}
                order={paymentModal.order}
                customerEmail={chatUser?.email || ""}
                paystackPublicKey={paymentModal.order?.paystackPublicKey || agentConfig?.paystackPublicKey || ""}
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentError={handlePaymentError}
                isLoading={isPaymentLoading}
            />

            {addressForm.isOpen && (
                <AddressForm
                    onSubmit={handleAddressSubmit}
                    onCancel={() => setAddressForm({ isOpen: false, product: null })}
                    isLoading={isAddressLoading}
                />
            )}

            {bookingForm.isOpen && (
                <BookingForm
                    service={bookingForm.service}
                    onSubmit={handleBookingSubmit}
                    onCancel={() => setBookingForm({ isOpen: false, service: null })}
                />
            )}

            <CartView
                isOpen={showCart}
                onClose={() => setShowCart(false)}
                items={cartItems}
                onUpdateQuantity={(id, quantity) => {
                    if (quantity === 0) {
                        setCartItems(prev => prev.filter(item => item.id !== id))
                    } else {
                        setCartItems(prev => prev.map(item => 
                            item.id === id ? { ...item, quantity } : item
                        ))
                    }
                }}
                onRemoveItem={(id) => {
                    setCartItems(prev => prev.filter(item => item.id !== id))
                }}
                onAddItem={(productName) => {
                    const product = products.find(p => 
                        p.name.toLowerCase().includes(productName.toLowerCase())
                    )
                    if (product) {
                        const existingItem = cartItems.find(item => item.id === product._id)
                        if (existingItem) {
                            setCartItems(prev => prev.map(item => 
                                item.id === product._id 
                                    ? { ...item, quantity: item.quantity + 1 }
                                    : item
                            ))
                        } else {
                            setCartItems(prev => [...prev, {
                                id: product._id,
                                name: product.name,
                                price: product.price,
                                quantity: 1,
                                image: product.images?.[0]
                            }])
                        }
                    }
                }}
                onCheckout={async () => {
                    if (cartItems.length === 0) return
                    
                    try {
                        // Create a combined order for all cart items
                        const total = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0)
                        const itemNames = cartItems.map(item => `${item.name} (x${item.quantity})`).join(', ')
                        
                        const order = await createOrder(
                            null, // No single product
                            null, // No delivery address for now
                            {
                                amount: total,
                                currency: products[0]?.currency || 'GHS',
                                itemName: `Cart Items: ${itemNames}`,
                                itemType: 'cart',
                                cartItems: cartItems
                            }
                        )
                        
                        setShowCart(false)
                        setCartItems([]) // Clear cart after checkout
                        setPaymentModal({ 
                            isOpen: true, 
                            product: { name: `Cart (${cartItems.length} items)` }, 
                            order 
                        })
                    } catch (error) {
                        alert(`Failed to create order: ${error instanceof Error ? error.message : 'Unknown error'}`)
                    }
                }}
                primaryColor={finalConfig.primaryColor}
                currency={products[0]?.currency || 'GHS'}
                position={finalConfig.position}
            />
        </div>
    )
}
